export function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", max = 180) {
  const clean = stripHtml(value);

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max - 1)}…`;
}

export function formatDateTime(value) {
  if (!value) return "Date inconnue";

  const raw = String(value).trim();

  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);

  if (!match) return "Date inconnue";

  const [, yyyy, mm, dd, hh, mi] = match;

  if (!hh || !mi) {
    return `${dd}/${mm}/${yyyy}`;
  }

  return `${dd}/${mm}/${yyyy} à ${hh}:${mi}`;
}

export function buildCommercesSearchParams({
  q,
  categorie,
  stateFilter,
  hasLogo,
  withGps,
  page,
  limit,
}) {
  return {
    q: q?.trim() || undefined,
    categorie: categorie?.trim() || undefined,
    is_active:
      stateFilter === "all"
        ? undefined
        : stateFilter === "active"
          ? true
          : false,
    has_logo: hasLogo === "all" ? undefined : hasLogo === "yes",
    with_gps: withGps === "yes" ? true : undefined,
    page,
    limit,
    sort: "nom_commerce",
    dir: "ASC",
  };
}

export function normalizeCommerceForm(form) {
  const gps = resolveCommerceGps(form);

  return {
    nom_commerce: form.nom_commerce.trim(),
    categorie: form.categorie?.trim() || "",
    siret: form.siret?.trim() || "",
    description_commerce: form.description_commerce || "",
    tags: form.tags?.trim() || "",
    contact_nom: form.contact_nom?.trim() || "",
    contact_fonction: form.contact_fonction?.trim() || "",
    tel: form.tel?.trim() || "",
    mail: form.mail?.trim() || "",
    website: form.website?.trim() || "",
    facebook: form.facebook?.trim() || "",
    instagram: form.instagram?.trim() || "",
    logo_url: form.logo_url?.trim() || "",
    photo_url: form.photo_url?.trim() || "",
    adresse: form.adresse?.trim() || "",
    code_postal: form.code_postal?.trim() || "",
    ville: form.ville?.trim() || "",

    /*
     * IMPORTANT :
     * aucune coordonnée = null
     * et surtout pas 0.
     */
    gps_lat: gps.gps_lat,
    gps_lng: gps.gps_lng,

    moyens_paiement: form.moyens_paiement?.trim() || "",
    accessibilite: form.accessibilite?.trim() || "",
    services: form.services?.trim() || "",
    is_active: !!form.is_active,
  };
}

export function validateCommerceForm(form) {
  const errors = {};

  if (!form.nom_commerce?.trim()) {
    errors.nom_commerce = "Le nom du commerce est obligatoire.";
  }

  if (form.mail?.trim()) {
    const email = form.mail.trim();
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!validEmail) {
      errors.mail = "L’adresse email n’est pas valide.";
    }
  }

  const gps = resolveCommerceGps(form);

  if (!gps.valid) {
    errors.gps_coordinates =
      "Les coordonnées GPS sont invalides. Format attendu : latitude, longitude.";

    errors.gps_lat = "Coordonnées GPS incomplètes ou invalides.";
    errors.gps_lng = "Coordonnées GPS incomplètes ou invalides.";
  }

  return errors;
}

export function normalizeExternalUrl(url = "") {
  const clean = String(url || "").trim();

  if (!clean) return "";

  if (
    clean.startsWith("http://") ||
    clean.startsWith("https://") ||
    clean.startsWith("mailto:") ||
    clean.startsWith("tel:")
  ) {
    return clean;
  }

  if (clean.startsWith("/")) return clean;

  return `https://${clean}`;
}

export function resolveCommerceGps(form = {}) {
  const combinedCoordinates = String(form.gps_coordinates || "").trim();

  /*
   * 1. Si le nouveau champ combiné est renseigné,
   * on le considère prioritaire.
   */
  if (combinedCoordinates) {
    const parsed = parseGpsCoordinates(combinedCoordinates);

    if (!parsed.valid) {
      return {
        gps_lat: null,
        gps_lng: null,
        valid: false,
      };
    }

    const lat = Number(parsed.gps_lat);
    const lng = Number(parsed.gps_lng);

    /*
     * Ancienne valeur parasite provenant de la BDD.
     * 0,0 est considéré ici comme "pas de coordonnées".
     */
    if (lat === 0 && lng === 0) {
      return {
        gps_lat: null,
        gps_lng: null,
        valid: true,
      };
    }

    return {
      gps_lat: parsed.gps_lat,
      gps_lng: parsed.gps_lng,
      valid: true,
    };
  }

  /*
   * 2. Compatibilité avec l'ancien formulaire
   * utilisant gps_lat + gps_lng séparément.
   */
  const rawLat =
    form.gps_lat !== null && form.gps_lat !== undefined
      ? String(form.gps_lat).trim()
      : "";

  const rawLng =
    form.gps_lng !== null && form.gps_lng !== undefined
      ? String(form.gps_lng).trim()
      : "";

  /*
   * Aucun GPS saisi.
   */
  if (!rawLat && !rawLng) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: true,
    };
  }

  /*
   * Une seule coordonnée renseignée = données incomplètes.
   */
  if (!rawLat || !rawLng) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: false,
    };
  }

  const lat = Number(rawLat);
  const lng = Number(rawLng);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: false,
    };
  }

  /*
   * Nettoyage des anciennes valeurs 0,0.
   */
  if (lat === 0 && lng === 0) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: true,
    };
  }

  return {
    gps_lat: String(lat),
    gps_lng: String(lng),
    valid: true,
  };
}

export function hasCommerceGps(item) {
  const rawLat = item?.gps_lat;
  const rawLng = item?.gps_lng;

  if (
    rawLat === null ||
    rawLat === undefined ||
    rawLat === "" ||
    rawLng === null ||
    rawLng === undefined ||
    rawLng === ""
  ) {
    return false;
  }

  const lat = Number(rawLat);
  const lng = Number(rawLng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return false;
  }

  /*
   * Evite que les anciens 0,0 apparaissent
   * dans les statistiques et sur la carte.
   */
  if (lat === 0 && lng === 0) {
    return false;
  }

  return true;
}

export function getGoogleMapsUrl(item) {
  if (!hasCommerceGps(item)) return "";

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${item.gps_lat},${item.gps_lng}`,
  )}`;
}

export function getCommerceAddress(item) {
  const parts = [
    item?.adresse,
    [item?.code_postal, item?.ville].filter(Boolean).join(" "),
  ].filter(Boolean);

  return parts.join("\n");
}

export function getCommerceTags(tags = "") {
  return String(tags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, 5);
}

export function getCommercesStats(items = [], total = 0) {
  const activeOnPage = items.filter((item) => !!item.is_active).length;
  const inactiveOnPage = items.filter((item) => !item.is_active).length;
  const withLogoOnPage = items.filter((item) => !!item.logo_url).length;
  const withPhotoOnPage = items.filter((item) => !!item.photo_url).length;
  const withGpsOnPage = items.filter((item) => hasCommerceGps(item)).length;
  const withContactOnPage = items.filter(
    (item) => !!item.mail || !!item.tel || !!item.contact_nom,
  ).length;

  return {
    total,
    activeOnPage,
    inactiveOnPage,
    withLogoOnPage,
    withPhotoOnPage,
    withGpsOnPage,
    withContactOnPage,
  };
}

export function formatGpsCoordinates(lat, lng) {
  const cleanLat =
    lat !== null && lat !== undefined && String(lat).trim() !== ""
      ? String(lat).trim()
      : "";

  const cleanLng =
    lng !== null && lng !== undefined && String(lng).trim() !== ""
      ? String(lng).trim()
      : "";

  if (!cleanLat && !cleanLng) return "";

  if (cleanLat && cleanLng) return `${cleanLat}, ${cleanLng}`;

  return cleanLat || cleanLng;
}

export function parseGpsCoordinates(value = "") {
  const clean = String(value || "")
    .trim()
    .replace(/\s+/g, " ");

  if (!clean) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: true,
    };
  }

  const parts = clean
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length !== 2) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: false,
    };
  }

  const lat = Number(parts[0]);
  const lng = Number(parts[1]);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: false,
    };
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return {
      gps_lat: null,
      gps_lng: null,
      valid: false,
    };
  }

  return {
    gps_lat: String(lat),
    gps_lng: String(lng),
    valid: true,
  };
}
